import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAccount } from '../../../contexts/Account';
import { AccountType } from '../../../services/utils/types';
import { postShare } from '../../../services/LinkedIn';
import LoadingLogoSkeleton from '../../SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../ACSnackbar';
import eventBus from '../../../eventBus';

const useStyles = () => ({
  container: {
    borderRadius: '10px',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
    maxWidth: '600px',
    padding: {
      xxs: '48px 0px 0px',
      xs: '30px',
    },
  },
  textHeadline: {
    fontWeight: '600',
    fontSize: '20px',
    padding: '8px 10px',
    color: '#4D586F',
  },
  textContent: {
    fontWeight: '400',
    fontSize: '14px',
    padding: '8px 10px',
    color: '#001030',
    padding: '0px',
    mb: '24px',
  },
  dialogActions: {
    width: '100%',
    marginTop: '8px',
    padding: '8px 12px',
  },
  mainButton: {
    width: '100%',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    height: '45px',
    borderRadius: '12px',
  },
});

export default function LinkedinModal() {
  const classes = useStyles();
  const { accountDetails, type } = useAccount();
  const { displayName } = accountDetails;
  const [linkedInPostTitle, setLinkedInPostTitle] = useState(
    `Join the ${displayName}'s Adisory Board!`
  );
  const [postBody, setBodyTitle] = useState(
    `I've built the ${displayName} Advisory Board on @advisorycloud, and would love for you to join as an advisor.`
  );
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [gotLinkedinResponse, setGotLinkedinResponse] = useState(false);
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  async function linkedInResponse(linkedinResponse) {
    if (linkedinResponse === 201) {
      setSuccessMessage('Your profile is shared successfully.');
      setSuccessSnackbar(true);
      setGotLinkedinResponse(true);
    } else if (linkedinResponse === 422) {
      setSuccessMessage(
        'You have already posted this content, either update the post or try again in 10 minutes'
      );
      setSuccessSnackbar(true);
      setGotLinkedinResponse(true);
    } else {
      setSuccessMessage('An error occurred while sharing your profile.');
      setSuccessSnackbar(true);
    }
  }

  async function postInLinkedIn() {
    setIsLoading(true);
    try {
      const postResponse = await postShare(
        linkedInPostTitle,
        postBody,
        `https://app.advisorycloud.com/join/advisors/${accountDetails.slug}/${accountDetails.id}`,
        'https://app.advisorycloud.com/images/linkedin-thumbnail.png',
        type === AccountType.COMPANY ? accountDetails.id : null
      );
      linkedInResponse(postResponse);
    } catch (error) {
      console.error('Error posting to LinkedIn:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleClose() {
    if (isLoading) return;
    setOpen(false);
  }

  useEffect(() => {
    eventBus.on('openLinkedInPostModal', () => {
      setOpen(true);
    });
    return () => {
      eventBus.remove('openLinkedInPostModal');
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isXS}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: classes.container,
      }}
    >
      <Close
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: '12px',
          top: '12px',
          color: '#4D586F',
        }}
      />
      <DialogTitle
        id="alert-dialog-title"
        display="flex"
        sx={{ ...classes.textHeadline, padding: '12px 12px' }}
      >
        <img
          src="/icons/linked_in_logo.svg"
          alt="LinkedIn Logo"
          style={{ marginRight: '12px' }}
        />
        Share on LinkedIn
      </DialogTitle>
      <DialogContent sx={{ padding: '8px 12px' }}>
        <Typography sx={classes.textContent}>
          We've made it easy to share your advisory board with your followers on
          LinkedIn, so your most engaged followers can join! Simply click below
          to post a message to your LinkedIn audience.
        </Typography>
        <TextField
          id="advisorHeadline"
          placeholder="Title"
          variant="outlined"
          fullWidth
          value={linkedInPostTitle}
          onChange={(evt) => setLinkedInPostTitle(evt.target.value)}
          inputProps={{
            maxLength: 200,
          }}
          sx={{
            marginBottom: '24px',
          }}
          InputProps={{
            style: {
              borderRadius: '8px',
            },
          }}
        />
        <TextField
          placeholder="Description"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={postBody}
          onChange={(evt) => setBodyTitle(evt.target.value)}
          InputProps={{
            style: {
              borderRadius: '8px',
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          color="primary"
          variant='contained'
          onClick={() => {
            postInLinkedIn();
          }}
          sx={{
            ...classes.mainButton,
            color: '#ffffff',
            backgroundColor: '#2B71F6',
            '&:hover': {
              backgroundColor: '#2B71F6',
            },
          }}
          disabled={
            !postBody || !linkedInPostTitle || isLoading || gotLinkedinResponse
          }
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Share on LinkedIn'}
        </Button>
      </DialogActions>
      <ACSnackbar
        open={successSnackbar}
        text={successMessage}
        severity="success"
        onClose={() => {
          setSuccessSnackbar(false);
          if (gotLinkedinResponse) {
            handleClose();
          }
        }}
        autoHideDuration={6000}
      />
    </Dialog>
  );
}
